import { useTheme } from 'styled-components';
import { ServicesGetCustomerStatisticsRequestParameters, useRepositoryQuery } from '@mews-commander/data-access';
import { LocalizePercentageOptions, lt, useLocalizePercentage } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { getToken } from '@optimus-web/common';
import { PieChartData } from '../../charts';

interface GetCustomerProfilesChartDataProps {
    value: number;
    textKey: TextKey;
    theme: ReturnType<typeof useTheme>;
    lp: (value: number, options?: LocalizePercentageOptions) => string;
}

const getCustomerProfilesChartData = ({ value, textKey, theme, lp }: GetCustomerProfilesChartDataProps) =>
    [
        {
            value,
            color: getToken('paletteBrandNight600')({ theme }),
            label: lt(textKey),
            valueLabel: lp(value, { maximumFractionDigits: 0 }),
        },
        {
            value: 1 - value,
            color: getToken('legacyTagBorderDenim')({ theme }),
            label: lt('NotCompleted'),
            valueLabel: lp(1 - value, { maximumFractionDigits: 0 }),
        },
    ] satisfies Array<PieChartData>;

export const useGetCustomerStatistics = ({
    serviceId,
}: Pick<ServicesGetCustomerStatisticsRequestParameters, 'serviceId'>) => {
    const lp = useLocalizePercentage();
    const theme = useTheme();

    const { data, ...rest } = useRepositoryQuery(
        'services',
        'getCustomerStatistics',
        { serviceId },
        {
            enabled: serviceId != null,
            refetchInterval: 15 * 60 * 1000,
            select: ({ arrival, inHouse }) => {
                return {
                    arrival: getCustomerProfilesChartData({
                        value: arrival,
                        textKey: TextKey.ArrivalCustomerProfileCompletion,
                        theme,
                        lp,
                    }),
                    inHouse: getCustomerProfilesChartData({
                        value: inHouse,
                        textKey: TextKey.InHouseCustomerProfileCompletion,
                        theme,
                        lp,
                    }),
                };
            },
        }
    );

    return { data, ...rest };
};
